import {
  Box,
  Button,
  HStack,
  Kbd,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
} from '@chakra-ui/react';

// Keyboard icon as inline SVG for crisp rendering
const KeyboardIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <line x1="6" y1="8" x2="6" y2="8" strokeLinecap="round" />
    <line x1="10" y1="8" x2="10" y2="8" strokeLinecap="round" />
    <line x1="14" y1="8" x2="14" y2="8" strokeLinecap="round" />
    <line x1="18" y1="8" x2="18" y2="8" strokeLinecap="round" />
    <line x1="6" y1="12" x2="6" y2="12" strokeLinecap="round" />
    <line x1="10" y1="12" x2="10" y2="12" strokeLinecap="round" />
    <line x1="14" y1="12" x2="14" y2="12" strokeLinecap="round" />
    <line x1="18" y1="12" x2="18" y2="12" strokeLinecap="round" />
    <line x1="8" y1="16" x2="16" y2="16" strokeLinecap="round" />
  </svg>
);

const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
const modKey = isMac ? '⌘' : 'Ctrl';

import { TDevSandboxMode } from './types';
import useSandboxTheme from './useSandboxTheme';

const getShortcuts = (mode?: TDevSandboxMode) => {
  const isScriptPractice = mode === 'script-practice';
  const base = [
    { keys: [modKey, 'Shift', 'S'], description: 'Save' },
    { keys: [modKey, 'Shift', 'F'], description: 'Format code' },
    { keys: [modKey, 'Shift', 'M'], description: 'Merlin AI' },
  ];
  if (!isScriptPractice) {
    base.push({ keys: [modKey, 'Shift', 'D'], description: 'Design overlay' });
    base.push({ keys: [modKey, 'Shift', 'A'], description: 'Assets' });
  }
  if (isScriptPractice) {
    base.push({ keys: [modKey, 'Shift', 'Enter'], description: 'Run tests' });
  }
  return base;
};

interface IShortcutsButtonProps {
  mode?: TDevSandboxMode;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  isActive?: boolean;
}

/**
 * Compact shortcuts button with keyboard icon.
 * Shows available keyboard shortcuts in a popover on click.
 * Shortcuts are contextual based on editor mode.
 */
const ShortcutsButton = ({ mode, isOpen, onOpen, onClose, isActive }: IShortcutsButtonProps) => {
  const shortcuts = getShortcuts(mode);
  const { colors } = useSandboxTheme();

  const isControlled = isOpen !== undefined;
  const showActive = isActive ?? isOpen;

  return (
    <Popover
      placement="top-start"
      trigger="click"
      {...(isControlled ? { isOpen, onOpen, onClose } : {})}
    >
      <PopoverTrigger>
        <Button
          aria-label="Keyboard shortcuts"
          leftIcon={<Box as="span" display="flex"><KeyboardIcon /></Box>}
          size="xs"
          variant="ghost"
          color={showActive ? colors.textSecondary : colors.textMuted}
          bg={showActive ? colors.controlBg : 'transparent'}
          h="28px"
          fontSize="11px"
          fontWeight="500"
          _hover={{ color: colors.textSecondary, bg: colors.controlBg }}
          _active={{ bg: colors.activeBg }}
        >
          Shortcuts
        </Button>
      </PopoverTrigger>
      <PopoverContent
        bg={colors.popoverBg}
        borderColor={colors.popoverBorder}
        w="240px"
        boxShadow={colors.popoverShadow}
        _focus={{ outline: 'none', boxShadow: colors.popoverShadow }}
      >
        <PopoverArrow bg={colors.popoverBg} boxShadow="none" />
        <PopoverBody py={3} px={3}>
          <Text fontSize="10px" color={colors.sectionLabel} fontWeight="600" letterSpacing="0.05em" mb={3} textTransform="uppercase">
            Shortcuts
          </Text>
          <VStack align="stretch" spacing={2}>
            {shortcuts.map((shortcut) => (
              <HStack key={shortcut.description} justify="space-between">
                <Text fontSize="xs" color={colors.textSecondary}>
                  {shortcut.description}
                </Text>
                <HStack spacing={1}>
                  {shortcut.keys.map((key, i) => (
                    <Kbd
                      key={i}
                      fontSize="10px"
                      bg={colors.controlBg}
                      borderColor={colors.controlBg}
                      borderWidth="1px"
                      color={colors.textSecondary}
                      py={0.5}
                      px={1.5}
                      borderRadius="3px"
                      fontWeight="500"
                    >
                      {key}
                    </Kbd>
                  ))}
                </HStack>
              </HStack>
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default ShortcutsButton;
